import { useMemo } from 'react';
import { useARInvoiceIndex, type ARInvoice, type ARInvoiceIndexRow } from '@/hooks/useAccountsReceivable';
import { arInvoiceReadOnlyReason, type ARInvoiceReadOnlyReason } from '@/lib/arInvoiceReadOnly';
import { isARIssued } from '@/lib/arInvoiceLifecycle';

export interface ARInvoiceEditability {
  /** Why the invoice opens read-only (`arInvoiceReadOnlyReason`); null = editable. */
  readOnlyReason: ARInvoiceReadOnlyReason | null;
  /**
   * The invoice's `ar_invoice_index` row — read for an issued invoice or one
   * with a posting (undefined for a draft, or while the index loads). The form hydrates the
   * AR control account from its `ar_account_id` (`_ar_posting_ar_leg`, the
   * one "which leg is the receivable" rule).
   */
  indexRow: ARInvoiceIndexRow | undefined;
  /** True while that index is still loading. */
  indexLoading: boolean;
}

/**
 * `arInvoiceReadOnlyReason` with the one fact the detail read does not carry:
 * `ar_invoice_index.in_filed_vat_period` (an issued invoice dated in a filed
 * VAT period, which the update RPC refuses to edit — `VAT_PERIOD_FILED`;
 * its fund moves through the panel's "Change fund" action instead).
 *
 * The form and BOTH editor hosts (`ARInvoicesTab`, `EditARInvoiceDialog`)
 * call this, so the banner the form shows and the submit the host withholds
 * never disagree. The org-wide index is read only for an issued invoice or
 * one with a posting (a draft is never in a filed period and has no posting) — the same complete slim index the Issue
 * dialog reads, shared through the TanStack cache.
 */
export function useARInvoiceEditability(
  organizationId: string | undefined,
  invoice: Parameters<typeof arInvoiceReadOnlyReason>[0] & Partial<Pick<ARInvoice, 'id'>>,
): ARInvoiceEditability {
  const issued = !!invoice?.status && isARIssued({ status: invoice.status });
  // Read for anything with a posting too (a cancelled invoice keeps its
  // posting, so its read-only form still shows its AR account).
  const needsIndex = issued || !!invoice?.ledger_transaction_id;
  const { data: indexRows, isLoading } = useARInvoiceIndex(needsIndex ? organizationId : undefined);
  const indexRow = useMemo(
    () => (invoice?.id ? indexRows?.find((row) => row.id === invoice.id) : undefined),
    [indexRows, invoice?.id],
  );
  const inFiledVatPeriod = !!indexRow?.in_filed_vat_period;
  const readOnlyReason = arInvoiceReadOnlyReason(invoice, inFiledVatPeriod);
  return {
    readOnlyReason,
    indexRow,
    indexLoading: needsIndex && !!organizationId && !!isLoading,
  };
}
